import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { merkLesen } from '../../hooks/useMerkZustand';
import { useKanzleiLeisteSichtbar } from './KanzleiLeiste';
import { AblageRechnungPruefen } from './AblageRechnungPruefen';
import { dateiFeld } from '../upload/DateiFelderDrop';

const endung = (n) => (n.split('.').pop() || '').toLowerCase();
const detail = (e) => e?.response?.data?.detail || e?.message || 'fehlgeschlagen';
const inTagen = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const post = (url, felder) => { const fd = new FormData(); Object.entries(felder).forEach(([k, v]) => v != null && fd.append(k, v)); return api.post(url, fd, { timeout: 180000 }); };

const kontextVon = async (pathname) => {
  const m = pathname.match(/^\/kanzlei\/mandanten\/(\d{5})/) || pathname.match(/^\/team-portal\/general_(\d+)/) || pathname.match(/^\/team-portal\/m\/(\d+)/);
  if (m) return { mnr: m[1], case_id: merkLesen(`m360-akte-${m[1]}`, '') || '' };
  const c = pathname.match(/^\/(?:team-portal|kanzlei\/akten)\/([^/]+)$/);
  if (c) { try { const r = await api.get(`/cases/${c[1]}`); return { mnr: r.data.mandanten_nr || '', case_id: c[1], az: r.data.aktenzeichen || '' }; } catch { return { case_id: c[1] }; } }
  return {};
};

const kategorie = (n) => { const e = endung(n); if (['eml', 'msg'].includes(e)) return 'E-Mail'; if (/ausweis|reisepass|pass\b|aufenthalt/i.test(n)) return 'Ausweis'; if (['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'tif', 'tiff'].includes(e)) return 'Scan / Foto'; return 'Dokument'; };

const alsDokument = async (f, ctx, mnr = ctx.mnr, case_id = ctx.case_id) => {
  await post('/documents/upload', { file: f, mandate_ref: ctx.az || (mnr ? `M-${mnr}` : 'Kanzlei-Ablage'), category: kategorie(f.name), register_mnr: mnr || '', register_case_id: mnr ? case_id || '' : '' });
  return mnr ? { text: `Dokument abgelegt bei M-${mnr}${case_id ? ' (Akte)' : ''}`, link: `/kanzlei/mandanten/${mnr}` } : { text: 'Dokument abgelegt – noch keinem Mandanten zugeordnet', link: '/kanzlei/mandanten/offen' };
};

const alsPdf = async (f, ctx) => {
  let e = null;
  try { e = (await post('/kanzlei/rechnungen/erkennen', { file: f })).data; } catch { return alsDokument(f, ctx); }
  if (e?.rechnungsnummer && e?.betrag && e?.mandanten_nr && e?.mandant_name) {
    const vorschlag = { mandanten_nr: e.mandanten_nr, rechnungsnummer: e.rechnungsnummer, betrag: String(e.betrag), faellig_am: e.faellig_am || inTagen(14),
      case_id: e.case_id || (ctx.mnr === e.mandanten_nr ? ctx.case_id || '' : ''), lastschrift: !!e.lastschrift };
    return { pruefen: true, warn: true, text: 'Rechnung erkannt – bitte prüfen', datei: f, erkannt: e, vorschlag, ctx };
  }
  return alsDokument(f, ctx, ctx.mnr || (e?.mandant_name ? e.mandanten_nr : ''), ctx.mnr ? ctx.case_id : e?.case_id);
};

const verteilen = async (f, ctx) => {
  const e = endung(f.name);
  if (e === 'ics') { const r = (await post('/kanzlei/outlook/import', { file: f })).data; return { text: `Outlook-Kalender: ${r.neu} neu, ${r.auto} automatisch zugeordnet`, link: '/kanzlei/outlook' }; }
  if (e === 'xml') {
    const kopf = (await f.slice(0, 4000).text()).toLowerCase();
    if (/camt\.05[234]/.test(kopf)) { const r = (await post('/kanzlei/bank/import', { file: f })).data; return { text: `Kontoauszug: ${r.neu ?? 0} neu, ${r.zugeordnet} zugeordnet, ${r.klaerung} zur Klärung`, link: '/kanzlei/zahlungen/abgleich' }; }
    if (/pain\.008/.test(kopf)) { const r = (await post('/kanzlei/sepa/datev-import', { file: f })).data; return { text: `DATEV-Lastschriften: ${r.zugeordnet} übernommen`, link: '/kanzlei/zahlungen/sepa-lastschrift' }; }
  }
  if (e === 'pdf') return alsPdf(f, ctx);
  return alsDokument(f, ctx);
};

const Ergebnisse = ({ liste, onClose }) => (
  <div className="fixed bottom-5 right-5 z-[70] w-[min(420px,calc(100vw-2.5rem))] rounded-2xl bg-[#0B1724] text-white shadow-2xl border border-[#b8893d]/40 p-4" data-testid="ablage-ergebnisse">
    <div className="flex items-center gap-2 mb-2"><UploadCloud className="w-4 h-4 text-[#e0b56a]" /><p className="text-sm font-semibold flex-1">Automatisch einsortiert</p>
      <button type="button" onClick={onClose} className="p-1 text-white/60 hover:text-white" aria-label="Schließen" data-testid="ablage-schliessen"><X className="w-4 h-4" /></button></div>
    <ul className="space-y-1.5 max-h-72 overflow-y-auto">{liste.map((x, i) => (
      <li key={`${x.name}-${i}`} className="flex items-start gap-2 text-xs" data-testid={`ablage-eintrag-${i}`}>
        {x.busy ? <Loader2 className="w-3.5 h-3.5 mt-0.5 animate-spin text-[#e0b56a]" /> : x.fehler || x.warn ? <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-400" /> : <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400" />}
        <span className="min-w-0 flex-1"><span className="block truncate text-white/60">{x.name}</span>
          {x.busy ? 'wird erkannt …' : x.fehler ? <span className="text-amber-300">Fehler: {x.fehler}</span> : x.link ? <Link to={x.link} className="text-[#e0b56a] hover:underline" onClick={onClose}>{x.text}</Link> : x.text}</span>
      </li>))}</ul>
  </div>
);

export const GlobaleAblage = () => {
  const sichtbar = useKanzleiLeisteSichtbar();
  const { pathname } = useLocation();
  const [ueber, setUeber] = useState(false);
  const [liste, setListe] = useState([]);
  const pfad = useRef(pathname);
  pfad.current = pathname;
  const zaehler = useRef(0);
  const verarbeiten = async (dateien) => {
    if (!dateien.length) return;
    const start = zaehler.current;
    zaehler.current += dateien.length;
    setListe((l) => [...l, ...dateien.map((f) => ({ name: f.name, busy: true }))]);
    const ctx = await kontextVon(pfad.current);
    for (let i = 0; i < dateien.length; i += 1) {
      let erg;
      try { erg = await verteilen(dateien[i], ctx); } catch (x) { erg = { fehler: detail(x) }; }
      setListe((l) => l.map((y, j) => (j === start + i ? { name: y.name, ...erg } : y)));
    }
    window.dispatchEvent(new Event('ablage-fertig'));
  };
  const verarbeitenRef = useRef(verarbeiten);
  verarbeitenRef.current = verarbeiten;
  const schliessen = () => { zaehler.current = 0; setListe([]); };
  const offenIdx = liste.findIndex((x) => x.pruefen);
  const offen = offenIdx >= 0 ? liste[offenIdx] : null;
  const fertig = (erg) => { setListe((l) => l.map((y, j) => (j === offenIdx ? { name: y.name, ...erg } : y))); window.dispatchEvent(new Event('ablage-fertig')); };
  const anlegen = async (w) => {
    const e = offen.erkannt;
    try {
      await post('/kanzlei/rechnungen', { file: offen.datei, ...w, betrag: String(w.betrag).replace(',', '.'), lastschrift: String(w.lastschrift), rechnungsdatum: e.rechnungsdatum || '' });
      fertig({ text: `Rechnung ${w.rechnungsnummer} für M-${w.mandanten_nr} angelegt`, link: '/kanzlei/zahlungen/rechnungen' });
    } catch (x) {
      if (x?.response?.status !== 409) throw x;
      fertig({ text: `Rechnung ${w.rechnungsnummer} war bereits vorhanden`, link: '/kanzlei/zahlungen/rechnungen', warn: true });
    }
  };
  const alsDok = async () => fertig(await alsDokument(offen.datei, offen.ctx, offen.ctx.mnr || offen.erkannt.mandanten_nr, offen.ctx.mnr ? offen.ctx.case_id : offen.erkannt.case_id));
  const verwerfen = () => fertig({ text: 'Verworfen – nichts angelegt', warn: true });

  useEffect(() => {
    if (!sichtbar) return undefined;
    let tiefe = 0;
    const istDatei = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
    const enter = (e) => { if (!istDatei(e) || e.target?.closest?.('[data-eigene-ablage]')) return; tiefe += 1; setUeber(true); };
    const leave = (e) => { if (!istDatei(e)) return; tiefe = Math.max(0, tiefe - 1); if (!tiefe || e.target?.closest?.('[data-eigene-ablage]')) setUeber(false); };
    const over = (e) => { if (!istDatei(e)) return; e.preventDefault(); setUeber(!dateiFeld(e.target) && !e.target?.closest?.('[data-eigene-ablage]')); };
    const drop = (e) => {
      tiefe = 0; setUeber(false);
      if (e.defaultPrevented || !istDatei(e)) return;
      e.preventDefault();
      verarbeitenRef.current([...e.dataTransfer.files]);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragleave', leave); window.removeEventListener('dragover', over); window.removeEventListener('drop', drop); };
  }, [sichtbar]);

  if (!sichtbar) return null;
  return (<>
    {ueber && (
      <div className="fixed inset-0 z-[65] pointer-events-none flex items-end justify-center p-6 bg-[#0B1724]/25 backdrop-blur-[2px]" data-testid="ablage-overlay">
        <div className="rounded-2xl border-2 border-dashed border-[#e0b56a] bg-[#0B1724]/95 text-white px-8 py-6 flex items-center gap-4 shadow-2xl">
          <UploadCloud className="w-8 h-8 text-[#e0b56a]" />
          <div><p className="font-serif text-2xl">Datei loslassen</p><p className="text-xs text-white/70 mt-0.5">Rechnungen, Kontoauszüge (camt), DATEV-Lastschriften, Outlook-Kalender und Dokumente werden automatisch einsortiert.</p></div>
        </div>
      </div>)}
    <input type="file" multiple className="hidden" data-testid="ablage-datei" onChange={(e) => { verarbeiten([...e.target.files]); e.target.value = ''; }} />
    {liste.length > 0 && <Ergebnisse liste={liste} onClose={schliessen} />}
    {offen && <AblageRechnungPruefen key={offenIdx} eintrag={offen} onAnlegen={anlegen} onDokument={alsDok} onVerwerfen={verwerfen} />}
  </>);
};

export default GlobaleAblage;
